/**
 * A song's waveform as a seek bar: played part in the accent colour, the rest
 * muted, click or drag to move. Drawn on a canvas sized to the element and the
 * screen's pixel ratio, so it stays sharp at any zoom and window size.
 * Peaks come from the engine's cache; until they arrive a flat line stands in.
 */

import { useEffect, useRef, useState } from "react";
import { fetchPeaks } from "../api";

const cache = new Map<string, number[]>();

function readVar(el: Element, name: string, fallback: string) {
  return getComputedStyle(el).getPropertyValue(name).trim() || fallback;
}

export function Waveform({
  trackId,
  position,
  duration,
  onSeek,
  height = 28,
  markers = [],
  className = "",
}: {
  trackId: string | null;
  position: number;
  duration: number;
  onSeek?: (seconds: number) => void;
  height?: number;
  markers?: { at: number; color: string; label: string }[];
  className?: string;
}) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const [peaks, setPeaks] = useState<number[] | null>(trackId ? cache.get(trackId) ?? null : null);
  const [width, setWidth] = useState(300);
  const [themeTick, setThemeTick] = useState(0);
  const dragging = useRef(false);

  useEffect(() => {
    if (!trackId) return setPeaks(null);
    const hit = cache.get(trackId);
    if (hit) return setPeaks(hit);
    setPeaks(null);
    const abort = new AbortController();
    void fetchPeaks(trackId, abort.signal).then((p) => {
      if (abort.signal.aborted || !p.peaks?.length) return;
      const values = p.peaks.map((v) => Math.abs(v) / 127);
      cache.set(trackId, values);
      setPeaks(values);
    });
    return () => abort.abort();
  }, [trackId]);

  useEffect(() => {
    const el = canvas.current?.parentElement;
    if (!el) return;
    const ro = new ResizeObserver(() => setWidth(Math.max(40, el.clientWidth)));
    ro.observe(el);
    // Redraw when the theme flips, since the colours are read from tokens.
    const mo = new MutationObserver(() => setThemeTick((n) => n + 1));
    mo.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });
    const mq = window.matchMedia?.("(prefers-color-scheme: dark)");
    const onMq = () => setThemeTick((n) => n + 1);
    mq?.addEventListener("change", onMq);
    return () => {
      ro.disconnect();
      mo.disconnect();
      mq?.removeEventListener("change", onMq);
    };
  }, []);

  useEffect(() => {
    const c = canvas.current;
    if (!c) return;
    const dpr = window.devicePixelRatio || 1;
    c.width = Math.round(width * dpr);
    c.height = Math.round(height * dpr);
    const g = c.getContext("2d");
    if (!g) return;
    g.setTransform(dpr, 0, 0, dpr, 0, 0);
    g.clearRect(0, 0, width, height);
    const played = readVar(c, "--wave-played", "#f5a524");
    const rest = readVar(c, "--wave", "#3a414c");
    const bar = 2;
    const gap = 1;
    const n = Math.max(1, Math.floor(width / (bar + gap)));
    const cut = duration > 0 ? (position / duration) * width : 0;
    for (let i = 0; i < n; i++) {
      let v = 0.08;
      if (peaks?.length) {
        const from = Math.floor((i / n) * peaks.length);
        const to = Math.max(from + 1, Math.floor(((i + 1) / n) * peaks.length));
        for (let k = from; k < to && k < peaks.length; k++) v = Math.max(v, peaks[k]);
      }
      const h = Math.max(2, v * height);
      const x = i * (bar + gap);
      g.fillStyle = x < cut ? played : rest;
      g.fillRect(x, (height - h) / 2, bar, h);
    }
    // The playhead: where the song is now, drawn over the bars.
    if (duration > 0 && position > 0) {
      g.fillStyle = readVar(c, "--fg-0", "#ffffff");
      g.fillRect(Math.min(width - 2, Math.max(0, Math.round(cut) - 1)), 0, 2, height);
    }
    for (const m of markers) {
      if (duration <= 0) continue;
      g.fillStyle = m.color;
      g.fillRect(Math.round((m.at / duration) * width), 0, 2, height);
    }
  }, [peaks, width, height, position, duration, markers, themeTick]);

  const seekFrom = (clientX: number) => {
    const c = canvas.current;
    if (!c || !onSeek || duration <= 0) return;
    const r = c.getBoundingClientRect();
    onSeek(Math.max(0, Math.min(1, (clientX - r.left) / r.width)) * duration);
  };

  return (
    <div className={`nv-wave ${className}`} style={{ height }}>
      <canvas
        ref={canvas}
        style={{ width: "100%", height }}
        role={onSeek ? "slider" : "img"}
        aria-label="Position in the song"
        aria-valuemin={0}
        aria-valuemax={Math.round(duration)}
        aria-valuenow={Math.round(position)}
        tabIndex={onSeek ? 0 : -1}
        onPointerDown={(e) => {
          if (!onSeek) return;
          dragging.current = true;
          e.currentTarget.setPointerCapture(e.pointerId);
          seekFrom(e.clientX);
        }}
        onPointerMove={(e) => dragging.current && seekFrom(e.clientX)}
        onPointerUp={() => (dragging.current = false)}
        onKeyDown={(e) => {
          if (!onSeek) return;
          if (e.key === "ArrowRight") onSeek(Math.min(duration, position + 5));
          if (e.key === "ArrowLeft") onSeek(Math.max(0, position - 5));
        }}
      />
    </div>
  );
}

export function clockTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) seconds = 0;
  const whole = Math.floor(seconds);
  const h = Math.floor(whole / 3600);
  const m = Math.floor((whole % 3600) / 60);
  const s = whole % 60;
  return h ? `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}` : `${m}:${String(s).padStart(2, "0")}`;
}
